// Agent 서버(cowork, user_sim) 공용 SSE 클라이언트 — 이벤트 형식 { event, data(JSON) }
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { axiosInstance } from "api/instance";
import { getCoachToken } from "api/coachAuth";

// Agent 서버로 보내는 사용자 인증 헤더. 코치 JWT 세션이면 Authorization: Bearer, 레거시면 X-Auth-Token.
// (cowork 는 에이전트가 /library 로 다른 활동지를 조회할 때 이 헤더를 메인 백엔드로 그대로 forward 한다)
export function authHeaders(): Record<string, string> {
  const coachToken = getCoachToken();
  if (coachToken) return { Authorization: `Bearer ${coachToken}` };
  const token = axiosInstance.defaults.headers["X-Auth-Token"];
  return typeof token === "string" && token ? { "X-Auth-Token": token } : {};
}

// fetch-event-source 는 callback 기반 — AsyncGenerator 로 변환 (queue + Promise resolver)
export async function* postSSE<T extends { event: string }>(
  url: string,
  body: unknown,
  signal?: AbortSignal,
  headers?: Record<string, string>
): AsyncGenerator<T> {
  const queue: T[] = [];
  let resolveNext: (() => void) | null = null;
  let done = false;
  let error: Error | null = null;

  const wakeup = () => {
    const r = resolveNext;
    resolveNext = null;
    r?.();
  };

  // fetch-event-source 는 외부 signal abort 시 promise 를 reject 가 아니라
  // resolve 하고 onclose 도 호출하지 않는다 (lib/cjs/fetch.js). 따라서 abort 만으로는
  // done 이 세팅되지 않아 아래 while 루프가 영원히 park 한다. signal 을 직접 구독해
  // done 을 세팅하고 깨워, abort 시 제너레이터가 확실히 종료되게 한다.
  if (signal) {
    if (signal.aborted) {
      done = true;
    } else {
      signal.addEventListener(
        "abort",
        () => {
          done = true;
          wakeup();
        },
        { once: true }
      );
    }
  }

  const fetchPromise = fetchEventSource(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...authHeaders(),
      ...headers,
    },
    body: JSON.stringify(body),
    signal,
    openWhenHidden: true,
    onmessage(msg) {
      if (!msg.event) return;
      try {
        const ev = {
          event: msg.event,
          data: JSON.parse(msg.data),
        } as unknown as T;
        queue.push(ev);
        wakeup();
      } catch {
        // malformed event — skip
      }
    },
    onerror(err) {
      error = err instanceof Error ? err : new Error(String(err));
      throw error; // 재시도 방지
    },
    onclose() {
      done = true;
      wakeup();
    },
  }).catch((e: unknown) => {
    error = e instanceof Error ? e : new Error(String(e));
    done = true;
    wakeup();
  });

  while (true) {
    while (queue.length) yield queue.shift()!;
    if (done) break;
    await new Promise<void>((r) => (resolveNext = r));
  }
  await fetchPromise;
  if (error) throw error;
}
